import { Card, CardActionArea, Box, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { useNavigate } from "react-router-dom";
import { trendColors } from "../palette";
import { StatCardData } from "../types";

const trendIcon = {
  up: <ArrowUpwardIcon sx={{ fontSize: 13 }} />,
  down: <ArrowDownwardIcon sx={{ fontSize: 13 }} />,
  flat: <RemoveIcon sx={{ fontSize: 13 }} />,
};

// A trend is only "good" (green) when its direction matches what the metric wants.
const isPositiveTrend = (data: StatCardData) => {
  if (data.trendDirection === "flat") return false;
  const wantsUp = data.trendMeaning === "goodWhenUp";
  return wantsUp ? data.trendDirection === "up" : data.trendDirection === "down";
};

// Value stays in dark ink and the icon in a neutral brand tint, so colour is reserved for
// the trend pill, the one place where green/red carries meaning (good vs bad change).
export const TrendStatCard = ({ data }: { data: StatCardData }) => {
  const navigate = useNavigate();
  const trendColor = data.trendDirection === "flat"
    ? trendColors.flat
    : isPositiveTrend(data) ? trendColors.up : trendColors.down;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid #e5e7eb",
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "#cfe0f5",
          boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      <CardActionArea
        onClick={() => navigate(data.route)}
        sx={{ height: "100%", p: 2.25, display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#334155", lineHeight: 1.3 }}>
              {data.heading}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
              {data.subHeading}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              backgroundColor: "rgba(25, 118, 210, 0.08)",
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {data.icon}
          </Box>
        </Box>

        <Typography
          sx={{
            mt: 2,
            fontSize: { xs: 26, md: 28 },
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {data.value}
        </Typography>

        <Box sx={{ mt: "auto", pt: 1.5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              color: trendColor,
              backgroundColor: `${trendColor}14`,
            }}
          >
            {trendIcon[data.trendDirection]}
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: trendColor, lineHeight: 1.6 }}>
              {data.trendLabel}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};
